<template>
  <v-card>
    <v-card-title class="cyan">
      <h3 class="headline white--text">pH</h3>
    </v-card-title>
    <v-card-text>
      <h4 class="subheading">
        <strong>GOAL RANGE:</strong>
        {{ settings.range.ph.low }} to {{ settings.range.ph.high }}
      </h4>
      <v-form>
        <v-layout row wrap>
          <v-flex class="pl-1 xs6">
            <v-text-field label="Tested" v-model="ph.tested" @input="update" />
          </v-flex>
          <v-flex class="pl-1 xs6">
            <v-text-field label="Goal" v-model="ph.goal" @input="update" />
          </v-flex>
          <v-flex class="pl-1 xs12">
            <v-select
              label="Lower"
              v-model="ph.lowerType"
              :items="ph.lowerTypes"
              item-text="name"
              :return-object="true"
              @input="update"
            />
          </v-flex>
          <v-flex class="pl-1 xs12">
            <v-select
              label="Raise"
              v-model="ph.raiseType"
              :items="ph.raiseTypes"
              item-text="name"
              :return-object="true"
              @input="update"
            />
          </v-flex>
          <a
            target="_blank"
            href="https://www.poolcalculator.com/pool-chemistry/ph/"
            >Learn more about pH »</a
          >
          <div v-if="ph.tested != ph.goal">
            <h4 class="subheading mt-3">
              <strong>SUGGESTION:</strong>
            </h4>
            <p v-if="ph.tested > ph.goal">
              Add {{ ph.vol }} {{ units.volume }} by volume of
              {{ ph.lowerType.name }}.
            </p>
            <p v-else-if="ph.oz > 0 && ph.vol > 0">
              Add {{ ph.oz }} {{ units.weight }} by weight or {{ ph.vol }}
              {{ units.volume }} by volume of {{ ph.raiseType }}.
            </p>
            <p v-else-if="ph.oz > 0">
              Add {{ ph.oz }} {{ units.weight }} by weight of
              {{ ph.raiseType }}.
            </p>
            <p v-else-if="ph.vol > 0">
              Add {{ ph.vol }} {{ units.volume }} by volume of
              {{ ph.raiseType }}.
            </p>
            <p class="mt-3">
              <span v-if="ph.oz > 0">
                {{ ph.oz }} {{ units.weight }} is
                {{ util.printWeight(ph.oz, settings.unitType) }}.
                <br />
              </span>
              <span v-if="ph.vol > 0"
                >{{ ph.vol }} {{ units.volume }} is
                {{ util.printVolume(ph.vol, settings.unitType) }}.</span
              >
            </p>
            <p>
              <strong>Note:</strong> pH calculations depend on TA and Borate.
              Changing pH will also change TA.
            </p>
            <PC-Dialog
              v-if="firebase.auth.currentUser"
              type="Log pH Addition"
              :fields="logFields"
              :showDialog="showDialog"
              @close="close"
              @save="save"
            >
              <template v-slot:button>
                <v-btn
                  block
                  color="cyan"
                  class="white--text mt-3"
                  @click="log()"
                  >Log Addition</v-btn
                >
              </template>
            </PC-Dialog>
          </div>
        </v-layout>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import PCDialog from "@/components/PCDialog";
import firebase from "@/firebase";
import util from "@/util";
export default {
  name: "PH",
  components: { PCDialog },
  data() {
    return {
      firebase,
      util,
      logFields: [
        {
          name: "Type",
          type: "select",
          value: "",
          items: [],
          classes: ["mt-2", "pl-1", "xs12"],
        },
        {
          name: "Amount",
          type: "text",
          value: "",
          classes: ["pl-1", "xs12", "md6"],
        },
        {
          name: "Units",
          type: "select",
          value: "",
          items: ["oz", "lbs", "fl oz", "gal", "g", "kg", "ml", "l"],
          classes: ["pl-1", "xs12", "md6"],
        },
      ],
      showDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      borate: "borate/getBorate",
      ph: "ph/getPH",
      ta: "ta/getTA",
      settings: "settings/getSettings",
    }),
    units() {
      return this.util.getUnits(this.settings.unitType);
    },
  },
  methods: {
    ...mapActions({
      initPH: "ph/initPH",
      savePH: "ph/savePH",
    }),
    ...mapMutations({
      calcPH: "ph/calcPH",
      setPH: "ph/setPH",
    }),
    close() {
      this.showDialog = false;
    },
    log() {
      this.logFields[0].items = [];
      if (this.ph.tested < this.ph.goal) {
        this.logFields[0].value = this.ph.raiseType;
        this.ph.raiseTypes.forEach((type) =>
          this.logFields[0].items.push(type)
        );
      } else {
        this.logFields[0].value = this.ph.lowerType;
        this.ph.lowerTypes.forEach((type) =>
          this.logFields[0].items.push(type)
        );
      }
      this.showDialog = true;
    },
    async save() {
      await firebase.db
        .collection("users")
        .doc(firebase.auth.currentUser.uid)
        .collection("logs")
        .add({
          pool: this.settings.pool.name,
          timestamp: new Date().toISOString(),
          chemical: this.logFields[0].value.name || this.logFields[0].value,
          amount: Number(this.logFields[1].value),
          units: this.logFields[2].value,
        });
      this.showDialog = false;
    },
    async update() {
      this.validateRange();
      this.setPH(this.ph);
      this.calcPH({
        borate: this.borate.tested,
        settings: this.settings,
        ta: this.ta.tested,
      });
      await this.savePH(this.ph);
    },
    validateRange() {
      if (this.ph.tested < 0) this.ph.tested = 0;
      if (this.ph.tested > 15) this.ph.tested = 15;
      if (this.ph.goal < 0) this.ph.goal = 0;
      if (this.ph.goal > 15) this.ph.goal = 15;
    },
  },
  async mounted() {
    await this.initPH();
  },
};
</script>
